import ensureAuthenticatedStorage from "@/contexts/ensureAuthenticated";
import { navigateToLogin } from "@/utils/navigation";
import { createStandaloneToast } from "@chakra-ui/react";
import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

// Cria uma instância separada para refresh token
const axiosRefresh = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

let isRefreshing = false;

let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

// Processa todas as requisições que ficaram em fila enquanto o refresh estava em andamento
const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(null);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  localStorage.removeItem("session");
  localStorage.removeItem("refreshToken");
  const { removeAuthentication } = ensureAuthenticatedStorage.getState();
  removeAuthentication();

  const { toast } = createStandaloneToast();
  toast({
    title: "Sessão expirada.",
    description: "Por favor faça o login, novamente.",
    status: "warning",
    duration: 5000,
    isClosable: true,
  });

  navigateToLogin();
};

axiosInstance.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    config.headers["X-Client-Full-URL"] = window.location.href;
  }
  return config;
});

// Interceptor de requisição para injetar Bearer Token caso exista
axiosInstance.interceptors.request.use((config) => {
  const session = localStorage.getItem("session");
  if (session && config.headers) {
    config.headers.Authorization = `Bearer ${session}`;
  }
  return config;
});

// Interceptor de resposta para tratar 401 e acionar refresh token
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      // Se já existe uma requisição de refresh em andamento, colocamos este request na fila
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          // Se não tiver refreshToken, deslogar
          processQueue(error);
          handleLogout();
          return reject(error);
        }

        axiosRefresh
          .post("/api/auth/refresh-token", {
            refreshToken,
          })
          .then(({ data }) => {
            // Salva os novos tokens no localStorage
            localStorage.setItem("session", data.session);
            localStorage.setItem("refreshToken", data.refreshToken);

            processQueue(null); // libera todos da fila

            // Reenvia o request original com o novo token
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${data.session}`;
            }
            resolve(axiosInstance(originalRequest));
          })
          .catch((err) => {
            processQueue(err);
            handleLogout();
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);
